
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PhaseTimeline } from '@/components/roadmap/PhaseTimeline';
import CardDeck from '@/components/roadmap/CardDeck';
import CanvasPreview from '@/components/roadmap/CanvasPreview';
import { cn } from '@/lib/utils';
import { InteractiveCard } from '@/components/landing/InteractiveCard';

const phases = [
  {
    id: 'phase-zero',
    title: 'Phase Zero',
    description: 'Onboarding early access users. Posts are all chronological on a feed. We\'re focused on building our community and gathering feedback.',
    isCompleted: false,
  },
  {
    id: 'phase-one',
    title: 'Phase One',
    description: 'Every 24 hours, we pick the best performing video to be vaulted. Vaulted videos become part of FOMO permanently. In addition, we will release a feature that allows exploration of content in a whole new way.',
    isCompleted: false,
  },
  {
    id: 'phase-two',
    title: 'Phase Two',
    description: 'Canvas, explore a map of content that moves away from boring content feeds, and features a new method of interaction and posting your creations.',
    isCompleted: false,
  }
];

const Roadmap = () => {
  const navigate = useNavigate();
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>('phase-zero'); // Changed initial state to 'phase-zero'

  const handlePhaseSelect = (phaseId: string) => {
    setSelectedPhaseId(prevId => prevId === phaseId ? null : phaseId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black">
      <div className="container max-w-5xl mx-auto pt-24 px-4 pb-16">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="mr-2" /> Back
        </Button>
        
        <div className="flex flex-col">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent mb-8">
            Our Roadmap
          </h1>
          
          <p className="text-muted-foreground mb-12 max-w-2xl">
            We're building FOMO in phases, each one bringing us closer to our vision of a community where content creators curate and celebrate top-tier content while earning rewards.
          </p>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className={cn(
              "transition-all duration-300",
              selectedPhaseId ? "lg:col-span-1" : "lg:col-span-2"
            )}>
              <PhaseTimeline 
                phases={phases} 
                currentPhaseId="phase-zero"
                selectedPhaseId={selectedPhaseId}
                onPhaseSelect={handlePhaseSelect}
                className="max-w-2xl" 
              />
            </div>
            
            {selectedPhaseId === 'phase-zero' && (
              <div className="lg:col-span-1 animate-fade-in">
                <div className="bg-black/20 rounded-xl p-5 backdrop-blur-sm border border-primary/10">
                  <h3 className="text-xl font-medium text-primary mb-3">Community Building</h3>
                  <p className="text-muted-foreground mb-4">
                    We're focused on building our community of creators and content enthusiasts.
                    The feed shows posts in chronological order as we gather feedback and refine the experience.
                  </p>
                  <div className="my-6">
                    <InteractiveCard />
                  </div>
                  <div className="rounded-lg bg-gradient-to-br from-primary/10 to-accent/10 h-12 flex items-center justify-center border border-primary/20 mt-4">
                    <p className="text-sm text-primary/70 font-medium">Current Phase</p>
                  </div>
                </div>
              </div>
            )}
            
            {selectedPhaseId === 'phase-one' && (
              <div className="lg:col-span-1 animate-fade-in">
                <div className="bg-black/20 rounded-xl p-5 backdrop-blur-sm border border-primary/10">
                  <h3 className="text-xl font-medium text-primary mb-3">Daily Vaulted Content</h3>
                  <p className="text-muted-foreground mb-4">
                    Every day, the best performing video is permanently vaulted in our collection. 
                    These gems become part of FOMO's exclusive library.
                  </p>
                  <CardDeck />
                </div>
              </div>
            )}
            
            {selectedPhaseId === 'phase-two' && (
              <div className="lg:col-span-1 animate-fade-in">
                <div className="bg-black/20 rounded-xl p-5 backdrop-blur-sm border border-primary/10">
                  <h3 className="text-xl font-medium text-primary mb-3">Canvas Experience</h3>
                  <p className="text-muted-foreground mb-4">
                    Move away from traditional content feeds with our innovative canvas approach. 
                    Explore content spatially and discover new ways to interact with creators.
                  </p>
                  <CanvasPreview />
                </div>
              </div>
            )}
            
            <div className="mt-16 p-6 bg-card/20 backdrop-blur-sm border border-border/30 rounded-lg max-w-2xl">
              <h2 className="text-xl font-semibold mb-3 text-primary">Want to shape our roadmap?</h2>
              <p className="text-muted-foreground mb-4">We value community input and are constantly evolving our plans based on feedback.</p>
              <Button onClick={() => window.open('https://discord.gg/ErRUrKBCcb', '_blank')}>
                Join our Discord
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roadmap;
